import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Formik, Form } from 'formik'
import isEmail from 'validator/lib/isEmail'
import axios from 'axios'
import { BackLink, FormField, PageHero, ResultDialog, SubmitButton } from '../../components/ui/ui'
import { prefersReducedMotion } from '../../utils/scroll'
import '../JobApplication/JobApplication.scss'
import './PricingOrder.scss'

const FORM_ENDPOINT = 'https://formspree.io/f/mrgdzgnr'

// Eases the displayed number toward `value` whenever it changes.
const useTween = (value, duration = 600) => {
  const [display, setDisplay] = useState(value)
  const fromRef = useRef(value)

  useEffect(() => {
    if (prefersReducedMotion()) { setDisplay(value); fromRef.current = value; return undefined }
    const from = fromRef.current
    let frame = 0
    let start = null
    const tick = (time) => {
      if (start === null) start = time
      const t = Math.min((time - start) / duration, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplay(Math.round(from + (value - from) * eased))
      if (t < 1) frame = requestAnimationFrame(tick)
      else fromRef.current = value
    }
    frame = requestAnimationFrame(tick)
    return () => { cancelAnimationFrame(frame); fromRef.current = value }
  }, [value, duration])

  return display
}

const buildMessage = (values, pkg, cycle) => {
  if (!values.full_name.trim()) return ''
  const price = cycle === 'monthly' ? pkg.monthlyPrice : pkg.yearlyPrice
  const company = values.company_name ? ` from ${values.company_name}` : ''
  const phone = values.phone ? `\n\nContact: ${values.phone}` : ''
  return `Hello,\n\nI'm ${values.full_name.trim()}${company}. I'd like to order the ${pkg.name} package (${cycle} billing: R${price.toLocaleString()}/${cycle === 'monthly' ? 'month' : 'year'}).${phone}\n\nI'd like to discuss implementation details and get started. Please let me know the next steps.\n\nThank you!`
}

const PricingOrder = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [status, setStatus] = useState('idle')
  const [dialog, setDialog] = useState(null)
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [userEditedMessage, setUserEditedMessage] = useState(false)

  const pkg = location.state?.package || { id: 'starter', name: 'Starter', monthlyPrice: 5000, yearlyPrice: 50000 }
  const price = billingCycle === 'monthly' ? pkg.monthlyPrice : pkg.yearlyPrice
  const shownPrice = useTween(price)
  const savings = Math.max(0, pkg.monthlyPrice * 12 - pkg.yearlyPrice)
  const savingsPct = pkg.monthlyPrice ? Math.round((savings / (pkg.monthlyPrice * 12)) * 100) : 0

  const initialValues = { full_name: '', email: '', company_name: '', phone: '', message: '' }

  const validate = (values) => {
    const errors = {}
    if (!values.full_name.trim()) errors.full_name = 'Your name is required'
    if (!values.email) errors.email = 'Your email is required'
    else if (!isEmail(values.email)) errors.email = "That email doesn't look right"
    if (values.phone && !/^[+\d][\d\s()-]{6,}$/.test(values.phone)) errors.phone = 'Use digits, spaces and an optional +'
    if (!values.message.trim()) errors.message = 'Add a short message'
    return errors
  }

  const onSubmit = async (values, { resetForm }) => {
    setStatus('loading')
    try {
      const response = await axios.post(FORM_ENDPOINT, {
        name: values.full_name,
        email: values.email,
        message: values.message || buildMessage(values, pkg, billingCycle),
        subject: `Pricing Order: ${pkg.name} Package`,
        package_name: pkg.name,
        package_id: pkg.id,
        billing_cycle: billingCycle,
        price,
        company: values.company_name,
        phone: values.phone
      })
      if (response.status !== 200) throw new Error('Unexpected response')
      setStatus('success')
      resetForm()
      setUserEditedMessage(false)
      setDialog({ type: 'success', title: 'Order placed!', message: 'Your order was sent. The team will be in touch shortly to discuss next steps.' })
    } catch (error) {
      setStatus('error')
      setDialog({ type: 'error', title: 'Order not sent', message: 'Something went wrong. Please try again in a moment.' })
    }
  }

  return (
    <main className="ui-page form-page">
      <PageHero
        back={<BackLink onClick={() => navigate('/services')}>Back to services</BackLink>}
        eyebrow="Place your order"
        title={<>Order the <span className="ui-gradient-text">{pkg.name}</span> package</>}
        subtitle="Choose a billing cycle, add your details, and a ready-to-send order message is drafted for you."
      />

      <section className="ui-container form-page__body">
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
          {(formik) => {
            const redraft = (values, cycle) => {
              if (userEditedMessage) return
              const draft = buildMessage(values, pkg, cycle)
              if (draft) formik.setFieldValue('message', draft)
            }
            const handleAutoField = (event) => {
              const { name, value } = event.target
              formik.setFieldValue(name, value)
              redraft({ ...formik.values, [name]: value }, billingCycle)
            }
            const changeCycle = (cycle) => {
              setBillingCycle(cycle)
              redraft(formik.values, cycle)
            }

            return (
              <div className="form-page__layout">
                <aside className="ui-card form-summary order-summary">
                  <div className="order-toggle" role="radiogroup" aria-label="Billing cycle">
                    <span className={`order-toggle__pill ${billingCycle === 'yearly' ? 'is-right' : ''}`} aria-hidden="true" />
                    <button type="button" role="radio" aria-checked={billingCycle === 'monthly'} className={billingCycle === 'monthly' ? 'is-active' : ''} onClick={() => changeCycle('monthly')}>Monthly</button>
                    <button type="button" role="radio" aria-checked={billingCycle === 'yearly'} className={billingCycle === 'yearly' ? 'is-active' : ''} onClick={() => changeCycle('yearly')}>
                      Yearly {savingsPct > 0 && <span className="order-toggle__save">-{savingsPct}%</span>}
                    </button>
                  </div>

                  <div className="order-price">
                    <span className="order-price__cur">R</span>
                    <span className="order-price__amt">{shownPrice.toLocaleString()}</span>
                    <span className="order-price__per">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                  </div>
                  {billingCycle === 'yearly' && savings > 0 && (
                    <p className="order-save"><i className="fas fa-tag" /> You save R{savings.toLocaleString()} a year</p>
                  )}

                  {pkg.features && (
                    <ul className="order-features">
                      {pkg.features.slice(0, 6).map((feature, index) => (
                        <li key={feature} style={{ '--i': index }}><i className="fas fa-check" /> {feature}</li>
                      ))}
                    </ul>
                  )}
                </aside>

                <div className="ui-card form-main">
                  <Form noValidate>
                    <div className="ui-form-grid">
                      <FormField name="full_name" label="Full name" autoComplete="name" required onChange={handleAutoField} />
                      <FormField name="email" label="Email address" type="email" autoComplete="email" required />
                      <FormField name="company_name" label="Company name" autoComplete="organization" hint="Optional" onChange={handleAutoField} />
                      <FormField name="phone" label="Phone number" type="tel" autoComplete="tel" hint="Optional" onChange={handleAutoField} />
                      <FormField
                        className="span-2"
                        name="message"
                        label="Order message"
                        as="textarea"
                        rows="8"
                        maxLength={3000}
                        required
                        hint={userEditedMessage ? 'Edited by you' : 'Drafted from your details and billing choice.'}
                        onChange={(event) => { formik.handleChange(event); setUserEditedMessage(true) }}
                      />
                    </div>
                    {userEditedMessage && (
                      <button
                        type="button"
                        className="form-regen"
                        onClick={() => { formik.setFieldValue('message', buildMessage(formik.values, pkg, billingCycle)); setUserEditedMessage(false) }}
                      >
                        <i className="fas fa-wand-magic-sparkles" /> Regenerate message
                      </button>
                    )}
                    <div className="form-actions">
                      <SubmitButton status={status} labels={{ idle: 'Place order', loading: 'Placing order…', success: 'Order placed!' }} />
                    </div>
                  </Form>
                </div>
              </div>
            )
          }}
        </Formik>
      </section>

      <ResultDialog
        open={Boolean(dialog)}
        {...(dialog || {})}
        onClose={() => {
          const succeeded = dialog?.type === 'success'
          setDialog(null)
          setStatus('idle')
          if (succeeded) navigate('/services')
        }}
      />
    </main>
  )
}

export default PricingOrder
